import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, CheckCircle, XCircle, PackageCheck, Send } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { ModuleGate } from "@/components/PlanNotIncluded";
import { fmtDate } from "@/utils/datetime";

type PRItem = { id: string; name: string; qty: number; unit: string | null; unit_price_minor: number; received_qty: number; inventory_item_id: string | null };
type PR = { id: string; code: string; status: string; vendor_id: string | null; branch_id: string | null; items: PRItem[]; total_minor: number; expense_id: string | null; notes: string | null; rejection_reason: string | null; created_at: string; approved_at: string | null; ordered_at: string | null; received_at: string | null };

const STATUS_ORDER = ["draft", "pending_approval", "approved", "ordered", "received", "closed"];
const STATUS_TONE: Record<string, { bg: string; fg: string }> = {
    draft: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
    pending_approval: { bg: "var(--warning-bg)", fg: "var(--warning-fg)" },
    approved: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    ordered: { bg: "var(--info-bg)", fg: "var(--info-fg)" },
    received: { bg: "var(--success-bg)", fg: "var(--success-fg)" },
    rejected: { bg: "var(--error-bg)", fg: "var(--error-fg)" },
    closed: { bg: "var(--neutral-bg)", fg: "var(--neutral-fg)" },
};

export default function PurchaseRequestDetail() {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const { hasPerm } = useAuth();
    const [pr, setPr] = useState<PR | null>(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState<string | null>(null);
    const [msg, setMsg] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [receiveLines, setReceiveLines] = useState<Record<string, number>>({});
    const [rejectReason, setRejectReason] = useState("");
    const [rejectOpen, setRejectOpen] = useState(false);

    const load = async () => {
        setLoading(true); setErr(null);
        try {
            const r = await api.get(`/procurement/requests/${id}`);
            setPr(r.data);
            const rl: Record<string, number> = {};
            for (const it of (r.data.items as PRItem[])) rl[it.id] = it.received_qty > 0 ? it.received_qty : it.qty;
            setReceiveLines(rl);
        } catch (e: any) { setErr(formatApiError(e)); } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const transition = async (to: string, reason?: string) => {
        if (!pr) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            await api.post(`/procurement/requests/${pr.id}/transition`, { to, reason: reason || null });
            setMsg(t("procurement.pr.msg.transitioned", { to: t(`procurement.pr.status.${to}`) }));
            setRejectOpen(false); setRejectReason("");
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    const receive = async () => {
        if (!pr) return;
        setBusy(true); setErr(null); setMsg(null);
        try {
            const lines = pr.items.map((it) => ({ item_id: it.id, received_qty: receiveLines[it.id] ?? 0 }));
            const r = await api.post(`/procurement/requests/${pr.id}/receive`, {
                lines, branch_id: pr.branch_id, create_expense: true,
            });
            setMsg(t("procurement.pr.msg.received", { total: (r.data.total_minor / 100).toFixed(2) }));
            await load();
        } catch (e: any) { setErr(formatApiError(e)); } finally { setBusy(false); }
    };

    if (loading) return <div className="flex justify-center p-6"><Loader2 className="animate-spin" size={22} /></div>;
    if (!pr) return <div className="rounded-md p-3 text-sm" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }}>{err || t("common.not_found")}</div>;

    const tone = STATUS_TONE[pr.status] || STATUS_TONE.draft;
    const canApprove = hasPerm("procurement.approve");
    const canEdit = hasPerm("procurement.edit");
    const canOrder = hasPerm("procurement.order");
    const canReceive = hasPerm("procurement.receive");

    return (
        <ModuleGate module="procurement">
            <div className="space-y-4" data-testid="proc-pr-detail">
                <div className="flex items-center gap-3">
                    <Link to="/procurement/requests" className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="proc-pr-back"><ArrowLeft size={16} className="inline" /> {t("common.back")}</Link>
                </div>
                <div className="rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }}>
                    <div className="flex items-center flex-wrap gap-3 mb-4">
                        <h1 className="text-xl font-semibold flex-1" style={{ color: "var(--text)" }}>{pr.code}</h1>
                        <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs" style={{ background: tone.bg, color: tone.fg }} data-testid="proc-pr-status">
                            <span aria-hidden="true">●</span>
                            <span>{t(`procurement.pr.status.${pr.status}`)}</span>
                        </span>
                    </div>
                    {msg && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--success-bg)", color: "var(--success-fg)" }} data-testid="proc-pr-msg">{msg}</div>}
                    {err && <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="proc-pr-detail-err">{err}</div>}
                    {pr.rejection_reason && (
                        <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--error-bg)", color: "var(--error-fg)" }} data-testid="proc-pr-reject-reason">
                            <strong>{t("procurement.pr.reject_reason")}:</strong> {pr.rejection_reason}
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mb-4" style={{ color: "var(--text-muted)" }}>
                        <div><div>{t("procurement.pr.created_at")}</div><div style={{ color: "var(--text)" }}>{fmtDate(pr.created_at)}</div></div>
                        <div><div>{t("procurement.pr.approved_at")}</div><div style={{ color: "var(--text)" }}>{pr.approved_at ? fmtDate(pr.approved_at) : "—"}</div></div>
                        <div><div>{t("procurement.pr.ordered_at")}</div><div style={{ color: "var(--text)" }}>{pr.ordered_at ? fmtDate(pr.ordered_at) : "—"}</div></div>
                        <div><div>{t("procurement.pr.received_at")}</div><div style={{ color: "var(--text)" }}>{pr.received_at ? fmtDate(pr.received_at) : "—"}</div></div>
                    </div>
                    {pr.notes && <div className="text-sm mb-4" style={{ color: "var(--text)" }}><strong>{t("common.notes")}:</strong> {pr.notes}</div>}
                    <div className="overflow-x-auto rounded-md border mb-4" style={{ borderColor: "var(--border)" }}>
                        <table className="w-full text-sm">
                            <thead>
                                <tr style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border)" }}>
                                    <th className="text-left px-3 py-2">{t("procurement.pr.item_name")}</th>
                                    <th className="text-right px-3 py-2">{t("procurement.pr.qty")}</th>
                                    <th className="text-right px-3 py-2">{t("procurement.pr.received")}</th>
                                    <th className="text-right px-3 py-2">{t("procurement.pr.price_minor")}</th>
                                    {(pr.status === "approved" || pr.status === "ordered") && canReceive && (
                                        <th className="text-right px-3 py-2">{t("procurement.pr.receive_qty")}</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {pr.items.map((it) => (
                                    <tr key={it.id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`proc-pr-item-${it.id}`}>
                                        <td className="px-3 py-2" style={{ color: "var(--text)" }}>{it.name}</td>
                                        <td className="px-3 py-2 text-right" style={{ color: "var(--text)" }}>{it.qty} {it.unit}</td>
                                        <td className="px-3 py-2 text-right" style={{ color: "var(--text-muted)" }}>{it.received_qty}</td>
                                        <td className="px-3 py-2 text-right font-mono" style={{ color: "var(--text)" }}>{(it.unit_price_minor / 100).toFixed(2)}</td>
                                        {(pr.status === "approved" || pr.status === "ordered") && canReceive && (
                                            <td className="px-3 py-2 text-right">
                                                <input type="number" step="0.01" min={0} value={receiveLines[it.id] ?? 0}
                                                    data-testid={`proc-pr-receive-qty-${it.id}`}
                                                    onChange={(e) => setReceiveLines({ ...receiveLines, [it.id]: parseFloat(e.target.value || "0") })}
                                                    className="w-20 rounded-md px-2 py-1 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }} />
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                <tr style={{ borderTop: "1px solid var(--border)" }}>
                                    <td colSpan={3} className="px-3 py-2 text-right text-sm font-semibold" style={{ color: "var(--text)" }}>{t("procurement.pr.total")}</td>
                                    <td className="px-3 py-2 text-right font-mono font-semibold" style={{ color: "var(--text)" }} data-testid="proc-pr-total">{(pr.total_minor / 100).toFixed(2)}</td>
                                    <td></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    {pr.expense_id && (
                        <div className="rounded-md p-2 text-xs mb-3" style={{ background: "var(--info-bg)", color: "var(--info-fg)" }} data-testid="proc-pr-expense-link">
                            ● {t("procurement.pr.linked_expense")}: <span className="font-mono">{pr.expense_id.slice(0, 8)}…</span>
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {pr.status === "draft" && canEdit && (
                            <button data-testid="proc-pr-submit" onClick={() => transition("pending_approval")} disabled={busy}
                                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                <Send size={14} /> {t("procurement.pr.action.submit")}
                            </button>
                        )}
                        {pr.status === "pending_approval" && canApprove && (
                            <>
                                <button data-testid="proc-pr-approve" onClick={() => transition("approved")} disabled={busy}
                                    className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: "var(--success-fg)", color: "var(--card)", minHeight: 40 }}>
                                    <CheckCircle size={14} /> {t("procurement.pr.action.approve")}
                                </button>
                                <button data-testid="proc-pr-reject" onClick={() => setRejectOpen(true)} disabled={busy}
                                    className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: "var(--error-fg)", color: "var(--card)", minHeight: 40 }}>
                                    <XCircle size={14} /> {t("procurement.pr.action.reject")}
                                </button>
                            </>
                        )}
                        {pr.status === "approved" && canOrder && (
                            <button data-testid="proc-pr-order" onClick={() => transition("ordered")} disabled={busy}
                                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                <Send size={14} /> {t("procurement.pr.action.order")}
                            </button>
                        )}
                        {(pr.status === "approved" || pr.status === "ordered") && canReceive && (
                            <button data-testid="proc-pr-receive-btn" onClick={receive} disabled={busy}
                                className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm" style={{ background: "var(--primary)", color: "var(--primary-fg)", minHeight: 40 }}>
                                <PackageCheck size={14} /> {t("procurement.pr.action.receive")}
                            </button>
                        )}
                        {pr.status === "received" && canEdit && (
                            <button data-testid="proc-pr-close" onClick={() => transition("closed")} disabled={busy}
                                className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", minHeight: 40 }}>
                                {t("procurement.pr.action.close")}
                            </button>
                        )}
                    </div>
                </div>
                {rejectOpen && (
                    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.55)" }} onClick={() => setRejectOpen(false)}>
                        <div className="w-full max-w-md rounded-xl border p-5" style={{ background: "var(--card)", borderColor: "var(--border)" }} onClick={(e) => e.stopPropagation()} data-testid="proc-pr-reject-modal">
                            <h2 className="text-lg font-semibold mb-3" style={{ color: "var(--text)" }}>{t("procurement.pr.reject_title")}</h2>
                            <textarea required rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                                data-testid="proc-pr-reject-textarea"
                                placeholder={t("procurement.pr.reject_placeholder")}
                                className="w-full rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text)", border: "1px solid var(--border)" }} />
                            <div className="flex justify-end gap-2 mt-3">
                                <button onClick={() => setRejectOpen(false)} data-testid="proc-pr-reject-cancel" className="rounded-md px-3 py-2 text-sm" style={{ background: "var(--input)", color: "var(--text-muted)", minHeight: 40 }}>{t("common.cancel")}</button>
                                <button onClick={() => rejectReason.trim() && transition("rejected", rejectReason.trim())} data-testid="proc-pr-reject-confirm"
                                    className="rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--error-fg)", color: "var(--card)", minHeight: 40 }}>
                                    {t("procurement.pr.action.reject")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </ModuleGate>
    );
}
